import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import OnboardingModal from './OnboardingModal';
import Logo from './Logo';
import MobileBottomBar from './MobileBottomBar';
import { Sheet, SheetContent } from './ui/sheet';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, ClipboardList, DollarSign, FileText,
  MapPin, Users, Settings, Upload,
  Menu, LogOut, X, Calculator,
  LayoutGrid, Clock, Zap, Settings2, Target, Phone,
  Bell, Kanban, Receipt, Tv,
} from 'lucide-react';

const navGroups = [
  {
    label: 'Overview',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/', roles: ['admin', 'standard_user', 'super_admin'] },
      { icon: Tv, label: 'Weekly Huddle', path: '/huddle', roles: ['admin', 'standard_user', 'super_admin'] },
    ]
  },
  {
    label: 'Data Entry',
    items: [
      { icon: ClipboardList, label: 'EOD Summary (Sales)', path: '/eod/new', roles: ['admin', 'standard_user', 'super_admin'] },
      { icon: Phone, label: 'EOD Summary (Call Center)', path: '/eod/callcenter', roles: ['admin', 'standard_user', 'super_admin'] },
      { icon: DollarSign, label: 'Record Sale', path: '/sales/new', roles: ['admin', 'standard_user', 'super_admin'] },
      { icon: Users, label: 'Record Show', path: '/shows/new', roles: ['admin', 'standard_user', 'super_admin'] },
    ]
  },
  {
    label: 'Records',
    items: [
      { icon: FileText, label: 'EOD Records', path: '/eod/records', roles: ['admin', 'standard_user', 'super_admin'] },
      { icon: FileText, label: 'Sales Records', path: '/sales/records', roles: ['admin', 'standard_user', 'super_admin'] },
    ]
  },
  {
    label: 'Patients',
    items: [
      { icon: Kanban, label: 'Patient Pipeline', path: '/pipeline', roles: ['admin', 'standard_user', 'super_admin'] },
      { icon: Receipt, label: 'A/R Reconciliation', path: '/ar', roles: ['admin', 'super_admin'] },
    ]
  },
  {
    label: 'Analytics',
    items: [
      { icon: Zap, label: 'Flow Calculator', path: '/calculator/flow', roles: ['admin', 'standard_user', 'super_admin'] },
      { icon: Calculator, label: 'Marketing ROI', path: '/marketing/calculator', roles: ['admin', 'super_admin'] },
      { icon: LayoutGrid, label: 'Location Compare', path: '/locations/compare', roles: ['admin', 'super_admin'] },
      { icon: Target, label: 'Monthly Goals', path: '/goals', roles: ['admin', 'super_admin'] },
    ]
  },
  {
    label: 'Administration',
    items: [
      { icon: MapPin, label: 'Locations', path: '/admin/locations', roles: ['admin', 'super_admin'] },
      { icon: Users, label: 'Team Members', path: '/admin/users', roles: ['admin', 'super_admin'] },
      { icon: Settings, label: 'Dropdowns', path: '/admin/dropdowns', roles: ['admin', 'super_admin'] },
      { icon: Upload, label: 'Import CSV', path: '/admin/import', roles: ['admin', 'super_admin'] },
      { icon: Clock, label: 'Audit Log', path: '/admin/audit-log', roles: ['admin', 'super_admin'] },
      { icon: Bell, label: 'Notifications', path: '/admin/notifications', roles: ['admin', 'super_admin'] },
    ]
  },
  {
    label: 'Account',
    items: [
      { icon: Settings2, label: 'Settings', path: '/settings', roles: ['admin', 'standard_user', 'super_admin'] },
    ]
  }
];

const NavItem = ({ item, isActive, onClick }) => {
  const Icon = item.icon;
  return (
    <button
      onClick={() => onClick(item.path)}
      className={`sidebar-nav-item w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left mb-0.5 ${isActive ? 'active' : ''}`}
      data-testid={`sidebar-nav-item-${item.path.replace(/\//g, '-')}`}
    >
      <Icon size={15} className="flex-shrink-0 opacity-80" />
      <span className="flex-1 truncate">{item.label}</span>
      {isActive && <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: '#C9A961' }} />}
    </button>
  );
};

const SidebarContent = ({ onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNav = (path) => { navigate(path); if (onClose) onClose(); };
  const handleLogout = () => { logout(); navigate('/login'); };

  const filteredGroups = navGroups.map(g => ({
    ...g,
    items: g.items.filter(i => i.roles.includes(user?.role))
  })).filter(g => g.items.length > 0);

  const isActive = (path) => path === '/'
    ? location.pathname === '/'
    : location.pathname.startsWith(path);

  const roleLabel = user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'Staff';

  return (
    <div className="flex flex-col h-full sidebar-bg sidebar-noise relative">
      <div className="relative z-10 flex items-center justify-between px-4 py-4 border-b" style={{ borderColor: 'rgba(201,169,97,0.12)' }}>
        <Logo size={28} showText={true} light={true} />
        {onClose && (
          <button onClick={onClose} className="p-1 rounded-md hover:bg-white/5" style={{ color: 'rgba(245,241,232,0.5)' }}>
            <X size={15} />
          </button>
        )}
      </div>

      <div className="relative z-10 px-3 py-3 border-b" style={{ borderColor: 'rgba(201,169,97,0.12)' }}>
        <div className="flex items-center gap-2.5 px-2 py-2 rounded-md" style={{ background: 'rgba(201,169,97,0.06)' }}>
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs"
            style={{ background: '#C9A961', color: '#0E0E0E' }}>
            {(user?.full_name || user?.username || 'U')[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium truncate leading-tight" style={{ color: '#F5F1E8' }}>
              {user?.full_name || user?.username}
            </p>
            <p className="text-xs leading-tight" style={{ color: 'rgba(245,241,232,0.45)' }}>{roleLabel}</p>
          </div>
        </div>
      </div>

      <nav className="relative z-10 flex-1 overflow-y-auto px-2 py-3">
        {filteredGroups.map((group) => (
          <div key={group.label} className="mb-5">
            <p className="section-label px-3 mb-1.5">{group.label}</p>
            {group.items.map((item) => (
              <NavItem
                key={item.path}
                item={item}
                isActive={isActive(item.path)}
                onClick={handleNav}
              />
            ))}
          </div>
        ))}
      </nav>

      <div className="relative z-10 px-2 py-3 border-t" style={{ borderColor: 'rgba(201,169,97,0.12)' }}>
        <button
          onClick={handleLogout}
          className="sidebar-nav-item w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left"
          data-testid="logout-button"
        >
          <LogOut size={15} className="opacity-80" />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    if (path.includes('/huddle')) return 'Weekly Huddle';
    if (path.includes('/pipeline')) return 'Patient Pipeline';
    if (path.includes('/ar')) return 'A/R Reconciliation';
    if (path.includes('/eod/new')) return 'EOD Summary — Sales';
    if (path.includes('/eod/callcenter')) return 'EOD Summary — Call Center';
    if (path.includes('/sales/new')) return 'Record Sale';
    if (path.includes('/eod/records')) return 'EOD Records';
    if (path.includes('/sales/records')) return 'Sales Records';
    if (path.includes('/marketing')) return 'Marketing ROI';
    if (path.includes('/locations/compare')) return 'Location Compare';
    if (path.includes('/goals')) return 'Monthly Goals';
    if (path.includes('/calculator/flow')) return 'Flow Calculator';
    if (path.includes('/admin/locations')) return 'Locations';
    if (path.includes('/admin/users')) return 'Team Members';
    if (path.includes('/admin/dropdowns')) return 'Dropdown Settings';
    if (path.includes('/admin/import')) return 'Import Data';
    if (path.includes('/admin/audit-log')) return 'Audit Log';
    if (path.includes('/admin/notifications')) return 'Notifications';
    if (path.includes('/settings')) return 'Settings';
    return 'Catalina Performance Hub';
  };

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#FAF8F4' }}>
      <aside className="hidden lg:flex flex-col w-64 flex-shrink-0">
        <SidebarContent />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64" style={{ background: '#0E0E0E', border: 'none' }}>
          <SidebarContent onClose={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="flex items-center gap-4 px-6 py-3.5 flex-shrink-0 page-header">
          <button
            className="lg:hidden p-1"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-button"
            style={{ color: '#0E0E0E' }}
          >
            <Menu size={20} />
          </button>
          <div className="flex-1">
            <h1 className="font-heading font-semibold text-base tracking-tight" style={{ color: '#0E0E0E' }}>{getPageTitle()}</h1>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold"
              style={{ background: '#0E0E0E', color: '#C9A961' }}>
              {(user?.full_name || user?.username || 'U')[0].toUpperCase()}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pb-20 lg:pb-0">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="px-4 sm:px-6 py-6"
          >
            <Outlet />
          </motion.div>
        </main>

        <MobileBottomBar />
      </div>

      {user && user.role === 'admin' && <OnboardingModal user={user} />}
    </div>
  );
}
